import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import MainLayout from '../components/MainLayout';
import { Calendar, Download, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { downloadCSV } from '../lib/exportUtils';
import { useActiveCompany } from '../hooks/useActiveCompany';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const fmt = (n) => (Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 1 });

const todayMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const MonthlyAttendance = () => {
  const [activeCompany] = useActiveCompany();
  const [selectedMonth, setSelectedMonth] = useState(todayMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchSummary = useCallback(async () => {
    if (!activeCompany) return;
    setLoading(true);
    try {
      const { data: res } = await axios.get(
        `${API_URL}/dashboards/attendance?company_id=${activeCompany}&month=${selectedMonth}`,
        { withCredentials: true },
      );
      setData(res);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load monthly summary');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [activeCompany, selectedMonth]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const stats = data?.status_breakdown || {};
  const totalRecords = data?.total_records || 0;
  const totalOt = data?.total_ot_hours || 0;
  const lateCount = data?.late_arrivals || 0;
  const presentPct = data?.present_percentage || 0;
  const distinctEmployees = useMemo(() => {
    // Best-effort proxy: total/30 days roughly = active employees seen this month.
    if (!data?.daily_trend?.length) return 0;
    const peak = Math.max(...data.daily_trend.map(
      (d) => (d.present || 0) + (d.absent || 0) + (d.leave || 0) + (d.other || 0),
    ));
    return peak;
  }, [data]);

  const exportSummary = () => {
    if (!data?.daily_trend?.length) {
      toast.error('No data to export');
      return;
    }
    downloadCSV(
      data.daily_trend.map((d) => ({
        Date: d.date,
        Present: d.present,
        Absent: d.absent,
        Leave: d.leave,
        Other: d.other,
      })),
      `monthly_attendance_${selectedMonth}.csv`,
    );
    toast.success('Monthly attendance exported');
  };

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="monthly-attendance-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
              <Calendar className="inline-block w-8 h-8 mr-2 mb-1" />
              Monthly Attendance Summary
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              Daily aggregated attendance counts for the selected month
            </p>
          </div>
          <Button
            variant="outline"
            data-testid="monthly-attendance-export-btn"
            onClick={exportSummary}
            disabled={!data?.daily_trend?.length}
          >
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Select Month</label>
          <input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            data-testid="month-selector"
            className="h-10 px-4 bg-white border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-[#0052FF] focus:border-[#0052FF]"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-4" data-testid="monthly-attendance-kpis">
          <Kpi label="Total Records" value={fmt(totalRecords)} />
          <Kpi label="Present %" value={`${presentPct}%`} accent="text-green-600" />
          <Kpi label="Late Arrivals" value={fmt(lateCount)} accent="text-amber-600" />
          <Kpi label="Total OT Hours" value={fmt(totalOt)} accent="text-purple-600" />
          <Kpi label="Peak Headcount" value={fmt(distinctEmployees)} accent="text-blue-600" />
        </div>

        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-700">Daily breakdown</h2>
            <span className="text-xs text-slate-500">{selectedMonth}</span>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
            </div>
          ) : !data?.daily_trend?.length ? (
            <div className="p-10 text-center text-slate-500" data-testid="monthly-attendance-empty">
              <Calendar className="w-10 h-10 mx-auto text-slate-300 mb-2" />
              <p className="text-sm">No attendance records for {selectedMonth} yet.</p>
              <p className="text-xs text-slate-400 mt-1">
                Upload attendance via <strong>Attendance → Upload / Import</strong>, or run the Calculation engine, and the daily breakdown will appear here.
              </p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                  <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Present</th>
                  <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Absent</th>
                  <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Leave</th>
                  <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Other</th>
                </tr>
              </thead>
              <tbody>
                {data.daily_trend.map((row) => (
                  <tr
                    key={row.date}
                    data-testid={`summary-row-${row.date}`}
                    className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                  >
                    <td className="py-3 px-4 text-sm text-slate-900 font-mono">{row.date}</td>
                    <td className="py-3 px-4 text-center text-sm text-green-600 font-mono font-semibold">{row.present || 0}</td>
                    <td className="py-3 px-4 text-center text-sm text-red-600 font-mono font-semibold">{row.absent || 0}</td>
                    <td className="py-3 px-4 text-center text-sm text-blue-600 font-mono font-semibold">{row.leave || 0}</td>
                    <td className="py-3 px-4 text-center text-sm text-slate-600 font-mono">{row.other || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <p className="text-xs text-slate-500">
          {Object.entries(stats).length > 0 && (
            <>
              Status breakdown:{' '}
              {Object.entries(stats).map(([k, v]) => (
                <span key={k} className="font-mono mr-3">
                  {k}=<b>{v}</b>
                </span>
              ))}
            </>
          )}
        </p>
      </div>
    </MainLayout>
  );
};

const Kpi = ({ label, value, accent = 'text-slate-900' }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-4">
    <p className="text-xs font-semibold uppercase text-slate-500">{label}</p>
    <p className={`text-2xl font-semibold ${accent} mt-1 font-mono`}>{value}</p>
  </div>
);

export default MonthlyAttendance;
